import { playerService } from '../../services/player';

type PlaybackOptions = {
  deviceId?: string;
  positionMs?: number;
};

class SpotifyService {
  private webPlaybackDeviceId: string | null = null;

  setWebPlaybackDevice(deviceId: string | null): void {
    this.webPlaybackDeviceId = deviceId;
  }

  getWebPlaybackDevice(): string | null {
    return this.webPlaybackDeviceId;
  }

  private resolveDeviceId(explicit?: string | null): string | undefined {
    return explicit ?? this.webPlaybackDeviceId ?? undefined;
  }

  async playTrack(trackId: string, options: PlaybackOptions = {}): Promise<void> {
    const deviceId = this.resolveDeviceId(options.deviceId);
    if (!deviceId) {
      throw new Error('No active Spotify device found for playback.');
    }

    const body: {
      uris: string[];
      position_ms?: number;
    } = {
      uris: [`spotify:track:${trackId}`],
    };

    if (typeof options.positionMs === 'number') {
      body.position_ms = Math.max(0, Math.floor(options.positionMs));
    }

    await this.startPlayback(body, { deviceId });
  }

  async pausePlayback(options: { deviceId?: string } = {}): Promise<void> {
    const deviceId = this.resolveDeviceId(options.deviceId);
    await playerService.pausePlayback(deviceId);
  }

  async startPlayback(
    body: {
      context_uri?: string;
      uris?: string[];
      offset?: { position: number };
      position_ms?: number;
    } = {},
    options: { deviceId?: string } = {}
  ): Promise<void> {
    const deviceId = this.resolveDeviceId(options.deviceId);
    await playerService.startPlayback(body, deviceId);
  }

  async resumePlayback(options: PlaybackOptions = {}): Promise<void> {
    const deviceId = this.resolveDeviceId(options.deviceId);
    if (!deviceId) {
      throw new Error('No active Spotify device ready to resume playback.');
    }

    const body: { position_ms?: number } = {};
    if (typeof options.positionMs === 'number') {
      body.position_ms = Math.max(0, Math.floor(options.positionMs));
    }

    await playerService.startPlayback(body, deviceId);
  }

  async seekToPosition(positionMs: number): Promise<void> {
    const safePosition = Math.max(0, Math.floor(positionMs));
    await playerService.seekToPosition(safePosition);
  }

  async nextTrack(): Promise<void> {
    await playerService.nextTrack();
  }

  async previousTrack(): Promise<void> {
    await playerService.previousTrack();
  }

  async setVolume(volumePercent: number, options: { deviceId?: string } = {}): Promise<void> {
    const deviceId = this.resolveDeviceId(options.deviceId);
    const safePercent = Math.min(Math.max(Math.round(volumePercent), 0), 100);
    await playerService.setVolume(safePercent, deviceId);
  }

  async transferPlayback(deviceId: string, shouldPlay: boolean = false): Promise<void> {
    this.webPlaybackDeviceId = deviceId;
    await playerService.transferPlayback(deviceId, shouldPlay);
  }

  async toggleShuffle(enabled: boolean): Promise<void> {
    await playerService.toggleShuffle(enabled);
  }

  async setRepeatMode(mode: 'track' | 'context' | 'off'): Promise<void> {
    await playerService.setRepeatMode(mode);
  }
}

export const spotifyService = new SpotifyService();
